import {useState} from 'react'
import {CircleUser as UserCircle, Mail, Shield, Search} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import {getRoleConfig, isTaskOverdue} from '../../utils/helpers'
import Avatar from '../common/Avatar'
import Badge from '../common/Badge'
import UserDetailModal from './UserDetailModal'
import PageHeader from '../common/PageHeader'

export default function UserList() {
  const {state} = useApp()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [teamId, setTeamId] = useState('all')
  const [workload, setWorkload] = useState('all')
  const [sort, setSort] = useState('name')
  const selectedUser = state.users.find((user) => user.id === selectedUserId)
  const query = search.trim().toLocaleLowerCase('pt-BR')
  const rows = state.users.map((user) => {
    const teams = state.teams.filter(
      (team) => team.memberIds.includes(user.id) || team.leadId === user.id
    )
    const tasks = state.tasks.filter((task) => task.assigneeIds.includes(user.id))
    const pending = tasks.filter((task) => task.status !== 'done')
    const overdue = pending.filter((task) => isTaskOverdue(task)).length
    return {user, teams, tasks, pending, overdue}
  })
  const users = rows
    .filter(
      ({user, teams, pending, overdue}) =>
        [user.name, user.email, getRoleConfig(user.role)?.label, ...teams.map((team) => team.name)]
          .join(' ')
          .toLocaleLowerCase('pt-BR')
          .includes(query) &&
        (teamId === 'all' ||
          (teamId === 'none' ? teams.length === 0 : teams.some((team) => team.id === teamId))) &&
        (workload === 'all' ||
          (workload === 'available'
            ? pending.length === 0
            : workload === 'overdue'
            ? overdue > 0
            : pending.length > 0))
    )
    .sort(
      (a, b) =>
        (sort === 'workload'
          ? b.pending.length - a.pending.length
          : sort === 'overdue'
          ? b.overdue - a.overdue
          : 0) || a.user.name.localeCompare(b.user.name, 'pt-BR')
    )
  const hasFilters = !!search || teamId !== 'all' || workload !== 'all'
  const clearFilters = () => {
    setSearch('')
    setTeamId('all')
    setWorkload('all')
  }

  return (
    <div className='devops-page'>
      <PageHeader
        eyebrow='Pessoas e responsabilidades'
        icon={UserCircle}
        title='Pessoas'
        description='Encontre profissionais, consulte suas equipes e identifique a distribuição de trabalho.'
        meta={
          <>
            <span>{state.users.length} pessoas</span>
            <span>
              {rows.filter((row) => row.pending.length === 0).length} sem tarefas pendentes
            </span>
            <span>{rows.filter((row) => row.overdue > 0).length} com tarefas atrasadas</span>
          </>
        }
      />
      <div className='devops-surface p-4 space-y-3'>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
            <Search size={17} className='text-slate-400 shrink-0' />
            <input
              aria-label='Buscar pessoas'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Buscar nome, e-mail, cargo ou equipe...'
              className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
            />
          </div>
          <select
            aria-label='Filtrar pessoas por equipe'
            value={teamId}
            onChange={(event) => setTeamId(event.target.value)}
            className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
          >
            <option value='all'>Todas as equipes</option>
            <option value='none'>Sem equipe</option>
            {state.teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <select
            aria-label='Filtrar pessoas por carga de trabalho'
            value={workload}
            onChange={(event) => setWorkload(event.target.value)}
            className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
          >
            <option value='all'>Qualquer carga</option>
            <option value='pending'>Com tarefas pendentes</option>
            <option value='available'>Sem tarefas pendentes</option>
            <option value='overdue'>Com tarefas atrasadas</option>
          </select>
          <select
            aria-label='Ordenar pessoas'
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
          >
            <option value='name'>Nome A–Z</option>
            <option value='workload'>Mais tarefas pendentes</option>
            <option value='overdue'>Mais tarefas atrasadas</option>
          </select>
        </div>
        <div className='flex items-center justify-between gap-3 text-xs text-slate-500'>
          <span role='status'>
            {users.length} de {state.users.length} pessoas · tarefas dos quadros disponíveis neste
            workspace
          </span>
          {hasFilters && (
            <button onClick={clearFilters} className='text-brand-600 font-medium'>
              Limpar filtros
            </button>
          )}
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
        {users.length === 0 && (
          <div className='devops-empty-state col-span-full'>
            <UserCircle size={28} />
            <div>
              <h3>Nenhuma pessoa encontrada</h3>
              <p>
                {state.users.length
                  ? 'Ajuste a busca ou os filtros para encontrar outras pessoas.'
                  : 'As pessoas com acesso ao workspace aparecerão aqui.'}
              </p>
            </div>
            {hasFilters && (
              <button className='devops-secondary-button' onClick={clearFilters}>
                Limpar filtros
              </button>
            )}
          </div>
        )}
        {users.map(({user, teams, tasks, pending, overdue}) => {
          const role = getRoleConfig(user.role)
          return (
            <button
              key={user.id}
              onClick={() => setSelectedUserId(user.id)}
              className='devops-card p-5 text-left w-full'
              aria-label={'Abrir perfil de ' + user.name}
            >
              <div className='flex items-start gap-3 mb-4'>
                <Avatar
                  name={user.name}
                  color={user.avatarColor}
                  size='xl'
                  isOnline={user.isOnline}
                />
                <div className='flex-1 min-w-0'>
                  <h3 className='text-base font-semibold text-slate-800 truncate'>{user.name}</h3>
                  <div className='flex items-center gap-1.5 text-xs text-slate-500 mt-1'>
                    <Mail size={12} className='shrink-0' />
                    <span className='truncate'>{user.email}</span>
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-2 mb-3'>
                <Shield size={13} className='text-slate-400' />
                <Badge className={role?.color}>{role?.label || 'Cargo não definido'}</Badge>
              </div>
              <div className='flex flex-wrap gap-1.5 mb-4 min-h-6'>
                {teams.map((team) => (
                  <span
                    key={team.id}
                    className='inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border'
                    style={{
                      borderColor: team.color + '40',
                      backgroundColor: team.color + '10',
                      color: team.color,
                    }}
                  >
                    {team.name}
                  </span>
                ))}
                {!teams.length && (
                  <span className='text-xs text-slate-400'>Sem equipe vinculada</span>
                )}
              </div>
              <div className='grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-xs'>
                <div>
                  <span className='block text-lg font-semibold text-slate-700'>{tasks.length}</span>
                  <span className='text-slate-500'>Tarefas</span>
                </div>
                <div>
                  <span className='block text-lg font-semibold text-brand-600'>
                    {pending.length}
                  </span>
                  <span className='text-slate-500'>Pendentes</span>
                </div>
                <div>
                  <span
                    className={
                      'block text-lg font-semibold ' + (overdue ? 'text-red-600' : 'text-slate-400')
                    }
                  >
                    {overdue}
                  </span>
                  <span className='text-slate-500'>Atrasadas</span>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUserId(null)} />
      )}
    </div>
  )
}
